import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Hash, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { COMPANY } from "@/config";

export const Contact = () => {
  const [form, setForm] = useState({ naam: "", telefoon: "", oppervlakte: "", bericht: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleCall = () => {
    if (COMPANY.phone) {
      window.location.href = `tel:${COMPANY.phone}`;
    } else {
      toast.info("Telefoonnummer wordt binnenkort toegevoegd. Laat gerust uw gegevens achter via het formulier.");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.naam.trim() || !form.telefoon.trim()) {
      toast.error("Vul minstens uw naam en telefoonnummer in.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(COMPANY.formspreeEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          _subject: "[Website Aanvraag] Nieuw project via sefco.be",
          Naam: form.naam,
          Telefoonnummer: form.telefoon,
          "Vierkante meters": form.oppervlakte,
          Bericht: form.bericht,
        }),
      });
      if (!res.ok) throw new Error("Formspree error");
      toast.success("Bedankt voor uw aanvraag! Sefco neemt zo snel mogelijk contact met u op.");
      setForm({ naam: "", telefoon: "", oppervlakte: "", bericht: "" });
    } catch (err) {
      toast.error("Er ging iets mis. Probeer het later opnieuw of bel ons.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-neutral-900 border border-neutral-700 px-4 py-3.5 text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-sefco-blue focus:border-transparent transition";

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 bg-sefco-bg">
      <div className="mx-auto max-w-7xl px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20">
        <div>
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-10 bg-sefco-blue" />
            <span className="text-sm uppercase tracking-[0.2em] text-zinc-400 font-medium">Neem contact op</span>
          </div>
          <h2 className="font-heading font-bold text-white text-3xl sm:text-5xl tracking-tight mb-6">
            Vraag uw <span className="text-sefco-blue">vrijblijvende</span> offerte aan
          </h2>
          <p className="text-zinc-400 leading-relaxed mb-10 max-w-md">
            Laat uw gegevens achter en ontvang snel een eerlijke prijsopgave voor uw platte dak of dakisolatie.
          </p>

          <div className="space-y-4 mb-10">
            <div className="flex items-center gap-4 border border-neutral-800 p-4">
              <Phone size={20} className="text-sefco-blue shrink-0" />
              <div>
                <div className="text-xs uppercase tracking-wider text-zinc-500">Telefoon</div>
                <div className="text-white font-medium">{COMPANY.phone || COMPANY.phoneDisplay}</div>
              </div>
            </div>
            <div className="flex items-center gap-4 border border-neutral-800 p-4">
              <Mail size={20} className="text-sefco-blue shrink-0" />
              <div>
                <div className="text-xs uppercase tracking-wider text-zinc-500">E-mail</div>
                <a href={`mailto:${COMPANY.email}`} data-testid="contact-email" className="text-white font-medium hover:text-sefco-blue transition-colors">
                  {COMPANY.email}
                </a>
              </div>
            </div>
            <div className="flex items-center gap-4 border border-neutral-800 p-4">
              <MapPin size={20} className="text-sefco-blue shrink-0" />
              <div>
                <div className="text-xs uppercase tracking-wider text-zinc-500">Adres</div>
                <div className="text-white font-medium">{COMPANY.address}</div>
              </div>
            </div>
            <div className="flex items-center gap-4 border border-neutral-800 p-4">
              <Hash size={20} className="text-sefco-blue shrink-0" />
              <div>
                <div className="text-xs uppercase tracking-wider text-zinc-500">Ondernemingsnummer</div>
                <div className="text-white font-medium" data-testid="contact-btw">{COMPANY.ondernemingsnummer}</div>
              </div>
            </div>
          </div>

          <button
            data-testid="call-us-button"
            onClick={handleCall}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-sefco-blue hover:bg-sefco-blueHover text-white font-bold px-8 py-4 tracking-wide transition-colors"
          >
            <Phone size={20} /> Bel ons direct
          </button>
        </div>

        <motion.form
          data-testid="quote-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-sefco-surface border border-neutral-800 p-8 md:p-10"
        >
          <h3 className="font-heading font-bold text-2xl text-white mb-8">Offerteformulier</h3>
          <div className="space-y-5">
            <div>
              <label className="block text-sm uppercase tracking-wider text-zinc-400 mb-2">Naam *</label>
              <input data-testid="input-naam" className={inputCls} value={form.naam} onChange={update("naam")} placeholder="Uw naam" />
            </div>
            <div>
              <label className="block text-sm uppercase tracking-wider text-zinc-400 mb-2">Telefoonnummer *</label>
              <input data-testid="input-telefoon" className={inputCls} value={form.telefoon} onChange={update("telefoon")} placeholder="+32 ..." />
            </div>
            <div>
              <label className="block text-sm uppercase tracking-wider text-zinc-400 mb-2">m² van het dak</label>
              <input data-testid="input-oppervlakte" className={inputCls} value={form.oppervlakte} onChange={update("oppervlakte")} placeholder="bv. 120 m²" />
            </div>
            <div>
              <label className="block text-sm uppercase tracking-wider text-zinc-400 mb-2">Bericht</label>
              <textarea data-testid="input-bericht" rows={4} className={inputCls} value={form.bericht} onChange={update("bericht")} placeholder="Vertel ons kort over uw project..." />
            </div>
            <button
              data-testid="quote-form-submit"
              type="submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-3 bg-sefco-blue hover:bg-sefco-blueHover disabled:opacity-60 text-white font-bold px-8 py-4 tracking-wide transition-colors"
            >
              {loading ? <><Loader2 size={20} className="animate-spin" /> Versturen...</> : <><Send size={18} /> Verstuur aanvraag</>}
            </button>
            <p className="text-xs text-zinc-500 text-center">Wij nemen zo snel mogelijk contact met u op. Volledig vrijblijvend.</p>
          </div>
        </motion.form>
      </div>
    </section>
  );
};
